import { v4 as uuid } from 'uuid';

import { MODULE_TYPE_TO_NAME, ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { ComputedProperty } from 'dashboard/lib/case-layouts/computed-properties';
import { BaseModule } from 'dashboard/lib/module';
import { LogicBlockOrV2 } from 'dashboard/lib/workflows';
import { EnvironmentType } from 'dashboard/models/environment';
import { Configuration, Tab } from 'dashboard/models/grid-layout';
import { UserAvailability } from 'dashboard/models/user';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import { ModuleAISummary } from 'dashboard/modules/AISummary';
import { ModuleButton } from 'dashboard/modules/Button';
import { ModuleCaseAttachments } from 'dashboard/modules/CaseAttachments';
import { ModuleCaseComments } from 'dashboard/modules/CaseComments';
import { ModuleCaseEmail } from 'dashboard/modules/CaseEmail';
import { ModuleCaseInfo } from 'dashboard/modules/CaseInfo';
import { ModuleChecklist } from 'dashboard/modules/Checklist';
import { ModuleDataDisplay } from 'dashboard/modules/DataDisplay';
import { ModuleDataTable } from 'dashboard/modules/DataTable';
import { ModuleDevice } from 'dashboard/modules/Device';
import { ModuleEventTimeline } from 'dashboard/modules/EventTimeline';
import { ModuleFieldUpdate } from 'dashboard/modules/FieldUpdate';
import { ModuleGraphV1 } from 'dashboard/modules/Graph';
import { ModuleGraphV2 } from 'dashboard/modules/GraphV2/model';
import { ModuleIframe } from 'dashboard/modules/Iframe/model';
import { ModuleInput } from 'dashboard/modules/Input';
import { ModuleInputSelect } from 'dashboard/modules/InputSelect';
import { ModuleInsightsSummary } from 'dashboard/modules/InsightsSummary/model';
import { ModuleKeyValue } from 'dashboard/modules/KeyValue';
import { ModuleLayoutByAccount } from 'dashboard/modules/LayoutByAccount';
import { ModuleListMatches } from 'dashboard/modules/ListMatches';
import { ModuleLocation } from 'dashboard/modules/Location';
import { ModuleMarkdown } from 'dashboard/modules/Markdown';
import { ModuleRelatedCases } from 'dashboard/modules/RelatedCases';
import { ModuleResource } from 'dashboard/modules/Resource';
import { ModuleResourceAccount } from 'dashboard/modules/ResourceAccount';
import { ModuleResourceInquiry } from 'dashboard/modules/ResourceInquiry';
import { ModuleResourceReport } from 'dashboard/modules/ResourceReport';
import { ModuleResourceReportRun } from 'dashboard/modules/ResourceReportRun';
import { ModuleResourceTransaction } from 'dashboard/modules/ResourceTransaction';
import { ResourceVerificationModules } from 'dashboard/modules/ResourceVerification/model';
import { ModuleSAR } from 'dashboard/modules/SAR/model';
import { ModuleSelfieSimilarity } from 'dashboard/modules/SimilaritySelfie/model';
import { ModuleTable } from 'dashboard/modules/Table/model';
import { ModuleTags } from 'dashboard/modules/Tags';
import { FieldSchema, FieldSchemaBase, FieldSchemaType } from 'lib/fields';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';

// Case field keys should not have spaces or special characters. Only allow alphanumeric + underscores.
export const VALID_CASE_FIELD_KEY_REGEX = /^[_a-zA-Z0-9]+$/;

export const generateModuleToken = () => `ctmod_${uuid()}`;
export const generateTabToken = () => `cttab_${uuid()}`;
export const generateChecklistItemToken = () => `ctci_${uuid()}`;
export const generateAssignmentPolicyToken = () => `ctap_${uuid()}`;
export const generateCaseStatusToken = () => {
  return (
    'state_' +
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
};
export const generateCaseEmailTemplateToken = () => `cmtp_${uuid()}`;
export const generateCaseCommentTemplateToken = () => `cctp_${uuid()}`;
export const generateSARNarrativeTemplateToken = () => `sntp_${uuid()}`;

export const displayModuleName = (module: BaseModule) => {
  if (module.type === ModuleTypes.Resource && 'field-key' in module) {
    switch (module['field-key']) {
      case 'inquiries':
        return 'Inquiry';
      case 'reports':
        return 'Report';
      case 'verifications':
        return 'Verification';
      case 'transactions':
        return 'Transaction';
      case 'accounts':
        return 'Account';
      default:
        return 'Resource';
    }
  }
  return MODULE_TYPE_TO_NAME[module.type];
};

export type AssignmentPolicyName =
  | 'none'
  | 'case_creator'
  | 'fewest_cases'
  | 'inquiry_creator'
  | 'round_robin';

export interface AssignmentPolicy {
  token: string;
  name: string;
  'policy-name': AssignmentPolicyName;
  'user-ids': string[];
  'team-ids': string[];
  'round-robin-current-index': number;
  'max-capacity': number | null;
  'unassign-cases-when-unavailable'?: boolean;
  'assign-unassigned-cases'?: boolean;
  conditional: LogicBlockOrV2;
  'assignment-order-by': 'created_at' | 'sla_expires_at';
}

export interface AssignmentPolicyFieldSchema {
  label: string;
  type: FieldSchemaType;
  options?: string[];
}

export enum CaseAssignmentPolicyFieldKey {
  AssigneeStatus = 'assignee_status',
  NumberOfAvailableUsers = 'number_of_available_users',
}

export interface CaseEmailTemplate {
  token: string;
  name: string;
  'creator-id': string;
  cc: string[];
  subject: string;
  body: string;
}

export interface CaseCommentTemplate {
  token: string;
  name: string;
  body: string;
}

export interface SARNarrativeTemplate {
  token: string;
  name: string;
  body: string;
  'updated-at': string;
}

export const CaseAssignmentPolicySchema: Record<
  CaseAssignmentPolicyFieldKey,
  AssignmentPolicyFieldSchema
> = {
  [CaseAssignmentPolicyFieldKey.AssigneeStatus]: {
    label: 'Assignee status',
    type: FieldSchemaType.Choices,
    options: Object.values(UserAvailability),
  },
  [CaseAssignmentPolicyFieldKey.NumberOfAvailableUsers]: {
    label: 'Number of available users',
    type: FieldSchemaType.Integer,
  },
};

export const OperatorsMap: Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>> = {
  [FieldSchemaType.Integer]: [
    WorkflowOperatorKey.Equals,
    WorkflowOperatorKey.DoesNotEqual,
    WorkflowOperatorKey.GreaterThan,
    WorkflowOperatorKey.GreaterThanOrEqual,
    WorkflowOperatorKey.LessThan,
    WorkflowOperatorKey.LessThanOrEqual,
  ],
  [FieldSchemaType.Choices]: [
    WorkflowOperatorKey.Equals,
    WorkflowOperatorKey.DoesNotEqual,
    WorkflowOperatorKey.In,
    WorkflowOperatorKey.NotIn,
  ],
};

export interface TriageConfiguration {
  'unassign-cases-when-unavailable': boolean;
  'policy-tokens-created-cases': string[];
  'policy-tokens-unassigned-cases': string[];
}

interface Subscriptions {
  'case-creation-email-addresses': string[];
}

export interface ChecklistItem {
  // TODO: (melissa) after we've migrated to cases 2.0, this should be a required attribute
  token?: string;
  // [chris] go/checklist-migration: ChecklistItem model will be deleted after migrating checklist items to fields.
  // We need field-key to support checklist item field schemas.
  'field-key'?: string;
  description: string;
  'required-for': string[];
}

export interface CaseState {
  token: string;
  name: string;
  color: CaseStateColor;
  'next-state-ids': string[];
  'end-state': boolean;
  'comment-required': boolean;
  'to-transition-rules': any[];
  'archived-at': string | null;
}

export interface StateTag {
  'environment-type': EnvironmentType;
  'state-token': string;
  'available-tags': string[];
  'is-tag-required': boolean;
  'is-multi-tags-allowed': boolean;
  'can-create-tags': boolean;
}

export enum CaseTemplateStatus {
  Active = 'active',
  Archived = 'archived',
}

export interface CaseLayoutTab extends Tab<CaseModule> {
  badge?: ComputedProperty;
}

export type CaseModule =
  | ModuleButton
  | ModuleCaseComments
  | ModuleCaseEmail
  | ModuleTags
  | ModuleEventTimeline
  | ModuleLocation
  | ModuleDevice
  | ModuleCaseInfo
  | ModuleChecklist
  | ModuleGraphV1
  | ModuleGraphV2
  | ModuleCaseAttachments
  | ModuleListMatches
  | ModuleKeyValue
  | ModuleMarkdown
  | ModuleDataDisplay
  | ModuleDataTable
  | ModuleInput
  | ModuleInputSelect
  | ModuleRelatedCases
  | ModuleResource
  | ModuleResourceAccount
  | ModuleResourceInquiry
  | ModuleResourceReport
  | ModuleResourceReportRun
  | ModuleResourceTransaction
  | ModuleTable
  | ResourceVerificationModules
  | ModuleLayoutByAccount
  | ModuleSAR
  | ModuleSelfieSimilarity
  | ModuleFieldUpdate
  | ModuleInsightsSummary
  | ModuleIframe
  | ModuleAISummary;

export interface CaseLayout extends Configuration<CaseModule> {
  tabs: CaseLayoutTab[];

  // TODO: (melissa) remove this flag once we've fully migrated customers
  // from column layout to grid layout. Customers on column layout have an undefined layout-type
  'layout-type'?: 'grid';
}

export interface CaseTemplateConfiguration {
  resolutions: string[];
  // TODO: (melissa) clean up assignment policy refactor migration
  // We'll need to change up the UI to not assume that there is only one assignment policy
  'assignment-policy'?: AssignmentPolicy;
  'assignment-policies': AssignmentPolicy[];
  'triage-configuration': TriageConfiguration;
  subscriptions: Subscriptions;
  'checklist-items': ChecklistItem[];
  'field-schema': FieldSchema[];
  states: CaseState[];
  'state-tags': StateTag[];
  'case-action-ids': string[];
  'case-layout-v2': CaseLayout;
  'case-message-templates': CaseEmailTemplate[];
  'case-comment-templates': CaseCommentTemplate[];
  'default-from-email-address'?: string;
  'sar-narrative-templates': SARNarrativeTemplate[];
}

export interface CaseTemplateAttributes {
  status: CaseTemplateStatus;
  name: string;
  'case-prefix': string;
  configuration: CaseTemplateConfiguration;

  'created-at': string;
  'updated-at': string;
  'archived-at'?: string;
  'initial-state': CaseState;
  'initial-state-case-count': number;
}

export interface CaseDataManagerObjectSchemas {
  common: Record<string, any>;
  'with-template': Record<string, Record<string, any>>;
}

export type CaseDataManagerCriteriaVariableSchemas = {
  case: CaseDataManagerObjectSchemas;
  'current-user': CaseDataManagerObjectSchemas;
};

export type CaseDataManagerCriteriaVariableKeys = keyof CaseDataManagerCriteriaVariableSchemas;

export interface CaseTemplateMeta {
  'case-data-manager-schema': CaseDataManagerCriteriaVariableSchemas;
}

export type CaseDataManagerSchemaOld = Partial<
  Record<CaseDataManagerCriteriaVariableKeys, Record<string, any>>
>;

// NB: The shape we are using in the frontend is remapped from the backend
export type CaseDataManagerSchema = {
  current_case: Record<string, any>;
  current_user: Record<string, any>;
};

export type CaseDataManagerSchemaKeys = keyof CaseDataManagerSchema;

export type CaseTemplateBasicsOptionState = Pick<
  CaseState,
  'name' | 'token' | 'next-state-ids' | 'comment-required' | 'end-state'
> & {
  archived: boolean;
};

export interface CaseTemplateBasicsOption {
  id: string;
  name: string;
  'case-prefix': string;
  status: CaseTemplateStatus;
  'initial-state-token': string;
  states: CaseTemplateBasicsOptionState[];
  'field-schema': Pick<FieldSchemaBase, 'key' | 'label' | 'type'>[];
}

export interface CaseTemplateBasicsMeta {
  basics: CaseTemplateBasicsOption[];
}

export interface CaseTemplateBasicsResponse {
  meta: CaseTemplateBasicsMeta;
}

export interface CaseTemplateRelationships {
  user?: Partial<ApiRelationship>;
  workflows?: ApiListRelationship<ApiModel.Workflow>;
  'case-variable-definitions'?: ApiListRelationship<ApiModel.CaseVariableDefinition>;
}

export type CaseTemplate = ApiResource<
  CaseTemplateAttributes,
  CaseTemplateRelationships,
  ApiModel.CaseTemplate
>;

// TODO: (chris) remove this once we've fully migrated off of using 'red' for case state colors.
export const CaseStateColorArray = [
  'red',
  'pink',
  'yellow',
  'orange',
  'grey',
  'purple',
  'green',
  'teal',
  'blue',
] as const;

export type CaseStateColor = (typeof CaseStateColorArray)[number];

// NB: We don't want customers to add anymore red stickers.
export const CaseStateColorSelectableArray = [
  'pink',
  'yellow',
  'orange',
  'grey',
  'purple',
  'green',
  'teal',
  'blue',
] as const;
